import {
  CHECK_FIELD_TO_METADATA_KEY,
  CHECK_FIELDS,
  CheckField,
} from "@/constants/check-fields";

type AuditLog = {
  current_entity_data: Record<string, unknown>;
  previous_entity_data: Record<string, unknown> | null | undefined;
};

export type CheckFieldDiff = {
  field: CheckField;
  changed: boolean;
  previousValue: unknown;
  currentValue: unknown;
};

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if ((a as unknown[]).length !== (b as unknown[]).length) return false;
    return (a as unknown[]).every((item, i) =>
      deepEqual(item, (b as unknown[])[i]),
    );
  }
  const keysA = Object.keys(a as object).sort();
  const keysB = Object.keys(b as object).sort();
  if (keysA.length !== keysB.length) return false;
  if (keysA.join(",") !== keysB.join(",")) return false;
  return keysA.every((k) =>
    deepEqual(
      (a as Record<string, unknown>)[k],
      (b as Record<string, unknown>)[k],
    ),
  );
}

/**
 * Returns diff info for checkFieldsUpdates fields whose timestamp changed
 * between previous and current entity data.
 *
 * The entity data has checkFieldsUpdates nested at metadata.checkFieldsUpdates
 * (not at the top level). Field values are read from metadata.<key> using
 * CHECK_FIELD_TO_METADATA_KEY (e.g. "price" → metadata.monthlyPrice).
 *
 * `changed = false` → timestamp refreshed, real value unchanged → "Verificado permanece"
 * `changed = true`  → timestamp and value both changed → "Verificado foi atualizado"
 *
 * Fields whose timestamp did NOT change are excluded from the result.
 */
export function getCheckFieldsDiff(log: AuditLog): CheckFieldDiff[] {
  const curr = (log.current_entity_data ?? {}) as Record<string, unknown>;
  const prev = (log.previous_entity_data ?? {}) as Record<string, unknown>;

  // Entity data may be nested under "metadata" or flat at the root level.
  // Prefer the nested structure when "metadata" exists; otherwise fall back to root.
  const currMeta = (
    curr["metadata"] != null
      ? (curr["metadata"] as Record<string, unknown>)
      : curr
  ) as Record<string, unknown>;
  const prevMeta = (
    prev["metadata"] != null
      ? (prev["metadata"] as Record<string, unknown>)
      : prev
  ) as Record<string, unknown>;

  const currCheckFields = (currMeta["checkFieldsUpdates"] ?? {}) as Record<
    string,
    unknown
  >;
  const prevCheckFields = (prevMeta["checkFieldsUpdates"] ?? {}) as Record<
    string,
    unknown
  >;

  return CHECK_FIELDS.filter((field) => {
    // Only include fields whose checkFieldsUpdates timestamp actually changed
    return currCheckFields[field] !== prevCheckFields[field];
  }).map((field) => {
    // Read real field values from metadata using the correct key mapping
    const metaKey = CHECK_FIELD_TO_METADATA_KEY[field];
    const currentValue = currMeta[metaKey];
    const previousValue = prevMeta[metaKey];
    const changed = !deepEqual(previousValue, currentValue);
    return { field, changed, previousValue, currentValue };
  });
}
